import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { categorizeMetrics } from '../../utils/metricsUtils';
import { formatTimeHMS } from '../../utils/timeUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import useOnClickOutside from '../../utils/useOnClickOutside';
import { isValidEmail } from '../../utils/textUtils';
import downIcon from '../../assets/icons/down_icon.svg';
import rightIcon from '../../assets/icons/right_icon.svg';
import alertCircleRed from '../../assets/icons/alert_circle_red.svg';
import filterBlack from '../../assets/icons/filter_black.svg';
import filterBlackOpen from '../../assets/icons/filter_black_open.svg';
// PAYMENT COMMENTED OUT
// import visa from '../../assets/icons/payments/visa.svg';
// import mastercard from '../../assets/icons/payments/mastercard.svg';
// import paypal from '../../assets/icons/payments/paypal.svg';
// import jcb from '../../assets/icons/payments/JCB.svg';
// import dci from '../../assets/icons/payments/DCI.svg';
// import americanExpress from '../../assets/icons/payments/american_express.svg';
// import discover from '../../assets/icons/payments/discover.svg';

const MetricsCartModal = ({ 
  isOpen, 
  onClose, 
  metricList, 
  onMetricToggle,
  allMetricsData,
  totalSegments = 0,
  totalCharacters = 0,
  segments = [],
  onCheckout,
  expandedCategoriesFromList = {
    standard: true,
    bertscore: false,
    comet: false,
    metricx: false
  }
}) => {
  const [expandedCategories, setExpandedCategories] = useState(expandedCategoriesFromList);
  // PAYMENT COMMENTED OUT
  // const [promocode, setPromocode] = useState('');
  // const [isPromocodeApplied, setIsPromocodeApplied] = useState(false);
  const [metricFilter, setMetricFilter] = useState('all');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const filterRef = useRef(null);
  const filterButtonRef = useRef(null);
  
  useOnClickOutside([filterRef, filterButtonRef], () => setIsFilterOpen(false));
  
  // Sync with props when modal opens
  useEffect(() => {
    if (isOpen) {
      setExpandedCategories({
        standard: true,
        bertscore: true,
        comet: true,
        metricx: true,
      });
    } else {
      setExpandedCategories({
        standard: false,
        bertscore: false,
        comet: false,
        metricx: false,
      });
      setEmail('');
      setEmailError('');
      setIsSubmitting(false);
      
      // PAYMENT COMMENTED OUT
      // setPromocode('');
      // setIsPromocodeApplied(false);
    }
  }, [isOpen, expandedCategoriesFromList]);

  const allMetrics = metricList || [];

  const getAvgTime = (avgRequestTimeSec) => {
    const BASELINE = 0.3;
    if (!avgRequestTimeSec || avgRequestTimeSec <= 0) {
      return BASELINE;
    }
    return avgRequestTimeSec;
  };

  const totalTimeSec = useMemo(() => {
    return allMetrics.reduce((total, metric) => {
      if (!metric.selected) return total;

      const originalMetric = allMetricsData?.find(
        m => (m.id?.toLowerCase() || '') === metric.key
      );

      const avgRequestTimeSec = getAvgTime(originalMetric?.avgRequestTimeSec);
      if (totalSegments > 0) {
        return total + (avgRequestTimeSec * totalSegments);
      }

      return total;
    }, 0);
    // eslint-disable-next-line
  }, [allMetrics, allMetricsData, totalSegments, segments]);

  const roundedTimeSec = Math.ceil(totalTimeSec);
  const estimatedDeliveryTime = formatTimeHMS(roundedTimeSec);

  const filterMetrics = (metrics, filterType) => {
    if (filterType === 'all') {
      return metrics;
    }
    
    return metrics.filter(metric => {
      const io = metric.io || {};
      
      if (filterType === 'requiresReference') {
        const isReferenceRequired = io.reference === 'required' || io.reference?.required === true;
        return isReferenceRequired;
      }
      
      if (filterType === 'requiresSource') {
        const isSourceRequired = io.source === 'required' || io.source?.required === true;
        return isSourceRequired;
      }
      
      if (filterType === 'requiresTarget') {
        const isTargetRequired = io.target === 'required' || io.target?.required === true;
        return isTargetRequired;
      }
      
      return true;
    });
  };
  
  const filteredMetrics = filterMetrics(allMetrics, metricFilter);
  const categories = categorizeMetrics(filteredMetrics);
  
  const filterOptions = [
    { value: 'all', label: 'All metrics' },
    { value: 'requiresReference', label: 'Requires Reference' },
    { value: 'requiresSource', label: 'Requires Source' },
    { value: 'requiresTarget', label: 'Requires Target' },
  ];

  // Check if required fields are filled for a metric
  const checkRequiredFields = (metric) => {
    if (!metric.selected || !segments || segments.length === 0) {
      return { isValid: true, missingFields: [] };
    }

    const io = metric.io || {};
    const isSourceRequired = io.source === 'required' || io.source?.required === true;
    const isTargetRequired = io.target === 'required' || io.target?.required === true;
    const isReferenceRequired = io.reference === 'required' || io.reference?.required === true;

    const missingFields = [];
    
    // Check if at least one segment has the required fields filled
    const hasSource = segments.some(seg => seg.source && seg.source.trim().length > 0);
    const hasTarget = segments.some(seg => seg.target && seg.target.trim().length > 0);
    const hasReference = segments.some(seg => seg.reference && seg.reference.trim().length > 0);

    if (isSourceRequired && !hasSource) {
      missingFields.push('source');
    }
    if (isTargetRequired && !hasTarget) {
      missingFields.push('target');
    }
    if (isReferenceRequired && !hasReference) {
      missingFields.push('reference');
    }

    return {
      isValid: missingFields.length === 0,
      missingFields
    };
  };

  // Get field label for tooltip
  const getFieldLabel = (field) => {
    const labels = {
      source: 'source text',
      target: 'target text',
      reference: 'reference text'
    };
    return labels[field] || field;
  };

  // PAYMENT COMMENTED OUT
  // const handleApplyPromocode = () => {
  //   if (promocode.trim() === 'b') {
  //     setIsPromocodeApplied(true);
  //   } else {
  //     setIsPromocodeApplied(false);
  //   }
  // };


  // Handle checkout button click
  const handleCheckoutClick = async () => {
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setEmailError('Email is required');
      return;
    }
    if (!isValidEmail(normalizedEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    setEmailError('');
    if (onCheckout) {
      setIsSubmitting(true);
      try {
        // PAYMENT COMMENTED OUT - no longer passing totalPrice, isPromocodeApplied
        // onCheckout(totalPrice, isPromocodeApplied);
        await onCheckout(normalizedEmail);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  //need to fix this
  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const isCurrentlyOpen = prev[category];
      const willBeOpen = !isCurrentlyOpen;
      
      return {
        ...prev,
        [category]: willBeOpen
      };
    });
  };

  const getCategoryState = (categoryMetrics) => {
    if (categoryMetrics.length === 0) return false;
    const selectedCount = categoryMetrics.filter(m => m && m.selected).length;
    if (selectedCount === 0) return false;
    if (selectedCount === categoryMetrics.length) return true;
    return 'indeterminate';
  };

  const handleCategoryToggle = (categoryMetrics) => {
    const selectedCount = categoryMetrics.filter(m => m && m.selected).length;
    const shouldSelect = selectedCount < categoryMetrics.length;
    
    categoryMetrics.forEach(metric => {
      if (metric && metric.key && metric.selected !== shouldSelect) {
        onMetricToggle(metric.key);
      }
    });
  };

  const renderCategoryRow = (categoryName, categoryKey, categoryMetrics) => {
    if (categoryMetrics.length === 0) return null;
    
    return (
      <React.Fragment key={categoryKey}>
        <tr className="border-t border-[#E2E8F0]">
          <td colSpan="6" className="p-0">
            <div 
              className="pl-3 py-1.5 bg-[#F9FAFB] cursor-default"
              onClick={() => toggleCategory(categoryKey)}
            >
              <div className="flex items-center space-x-2">
                <div 
                  onClick={(e) => { e.stopPropagation(); toggleCategory(categoryKey); }}
                  className="flex items-center justify-center"
                >
                  <img 
                    src={expandedCategories[categoryKey] ? downIcon : rightIcon} 
                    alt={expandedCategories[categoryKey] ? "Collapse" : "Expand"}
                    className="w-4 h-4"
                  />
                </div>
                <Checkbox
                  checked={getCategoryState(categoryMetrics)}
                  onCheckedChange={() => handleCategoryToggle(categoryMetrics)}
                  onClick={(e) => e.stopPropagation()}
                />
                <div className="flex-1 h-[20px] flex items-center">
                  <span className="text-sm text-[#020817]">
                    {categoryName} <span className="text-[#9CA3AF]">({categoryMetrics.length})</span>
                  </span>
                </div>
              </div>
            </div>
          </td>
        </tr>
        
        {/* Metric Rows */}
        {expandedCategories[categoryKey] && categoryMetrics.map((metric) => {
          const io = metric.io || {};
          const isSourceRequired = io.source === 'required' || io.source?.required === true;
          const isTargetRequired = io.target === 'required' || io.target?.required === true;
          const isReferenceRequired = io.reference === 'required' || io.reference?.required === true;
          
          const validation = checkRequiredFields(metric);
          const hasWarning = !validation.isValid;
          const missingFieldsText = validation.missingFields.map(field => getFieldLabel(field)).join(', ');
          // PAYMENT COMMENTED OUT
          // const rawCost = costMap[metric.key] || 0;
          // const displayCost = categoryKey === 'standard' && rawCost === 0 ? 'Free' : `$${rawCost}`;
          
          return (
            <tr
              key={metric.key}
              className="hover:bg-[#F1F5F9] cursor-pointer border-t border-[#E2E8F0]"
              onClick={() => onMetricToggle(metric.key)}
            >
              <td className="py-1.5 pl-14 pr-2 leading-4 border-r border-[#E2E8F0] w-[230px]">
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={Boolean(metric.selected)}
                    onCheckedChange={() => onMetricToggle(metric.key)}
                    onClick={(e) => e.stopPropagation()}
                  />
                  <span className="text-sm text-[#020817]">{metric.name}</span>
                  {hasWarning && (
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            onClick={(e) => e.stopPropagation()}
                            className="p-0 m-0 bg-transparent border-none cursor-pointer"
                          >
                          <img src={alertCircleRed} color="#DC2626" alt="alert" className="h-4 w-4" />
                          </button>
                        </TooltipTrigger>
                        <TooltipContent 
                          side="bottom" 
                          className="max-w-[280px] px-3 py-2 border border-[#E2E8F0] rounded-sm shadow-[0px_1px_3px_0px_#0000001A]"
                        >
                          <p className="text-xs leading-5 text-[#020817]">
                            {metric.name} requires {missingFieldsText}, so without it the metric won't be processed and you won't be charged.
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </div>
              </td>
              <td className="py-1.5 pl-3 pr-2 leading-4 border-r border-[#E2E8F0] w-[80px]">
                <span className="text-sm text-[#6B7280]">{isSourceRequired ? 'Required' : '-'}</span>
              </td>
              <td className="py-1.5 pl-3 pr-2 leading-4 border-r border-[#E2E8F0] w-[80px]">
                <span className="text-sm text-[#6B7280]">{isTargetRequired ? 'Required' : '-'}</span>
              </td>
              <td className="py-1.5 pl-3 pr-2 leading-4 border-r border-[#E2E8F0] w-[80px]">
                <span className="text-sm text-[#6B7280]">{isReferenceRequired ? 'Required' : '-'}</span>
              </td>
              <td className="py-1.5 pl-3 pr-2 leading-4 border-r border-[#E2E8F0] w-[120px]">
                <span className="text-sm text-[#6B7280]">
                  {categoryKey === 'standard' ? 'Up to 1h' : 'Up to 3h'}
                </span>
              </td>
              <td className="py-1.5 pl-3 pr-2 leading-4 w-[300px]">
                <span className="text-sm text-[#6B7280] whitespace-normal break-words">{metric.description || '-'}</span>
              </td>
              {/* PAYMENT COMMENTED OUT
              <td className="py-1.5 pl-2 pr-3 leading-4 w-[76px] text-center tabular-nums">
                <span className="text-sm text-[#020817]">{displayCost}</span>
              </td>
              */}
            </tr>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="w-full max-w-[1000px] flex flex-col min-h-0 h-[min(calc(100vh-80px),92dvh)] max-h-[min(calc(100vh-80px),92dvh)] sm:h-[min(calc(100vh-80px),92dvh)] sm:max-h-[min(calc(100vh-80px),92dvh)] overflow-hidden gap-0 p-0"
      >
        <DialogHeader className="shrink-0 p-0 pt-2 pb-2 sm:pb-2mb-0">
          <DialogTitle className="text-lg font-medium text-[#020817]">
            Metrics Cart
          </DialogTitle>  
        </DialogHeader>

        {/* Scrollable metrics table — grows between title and footer so checkout never covers it */}
        <div className="flex-1 min-h-0 flex flex-col pb-0">
          <div className="flex-1 min-h-0 overflow-auto rounded-lg border border-[#E2E8F0]">
          <table className="w-full border-collapse table-fixed">
            <thead className="sticky top-0 z-10">
              <tr className="border-b border-[#E2E8F0] bg-[#F9FAFB]">
                <th className="text-left text-sm font-medium text-[#020817] py-1.5 pl-3 pr-2 border-r border-[#E2E8F0] w-[230px]">
                  <div className="flex items-center gap-1 relative">
                    <span>Metric</span>
                    <div className="relative h-[20px] w-[20px] flex items-center justify-center">
                      <button
                        ref={filterButtonRef}
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsFilterOpen(!isFilterOpen);
                        }}
                        className="p-0 m-0 bg-transparent border-none cursor-pointer relative"
                      >
                      <img src={metricFilter !== 'all' ? filterBlackOpen : filterBlack} alt="Filter" />
                      </button>
                      {isFilterOpen && (
                        <div 
                          ref={filterRef}
                          className="absolute top-full left-0 mt-1 bg-white p-1 rounded-md border border-[#E2E8F0] shadow-[0px_1px_3px_0px_#0000001A] z-50 min-w-[160px]"
                        >
                          {filterOptions.map((option) => (
                            <div
                              key={option.value}
                              onClick={() => {
                                setMetricFilter(option.value);
                                setIsFilterOpen(false);
                              }}
                              className={`px-3 py-2 mb-0.5 last:mb-0 text-sm cursor-pointer hover:bg-[#F1F5F9] ${
                                metricFilter === option.value ? 'bg-[#E2E8F0] font-medium rounded-md' : 'font-normal'
                              }`}
                            >
                              {option.label}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </th>
                <th className="text-left text-sm font-medium text-[#020817] py-1.5 pl-3 pr-2 border-r border-[#E2E8F0] w-[80px]">
                  Source
                </th>
                <th className="text-left text-sm font-medium text-[#020817] py-1.5 pl-3 pr-2 border-r border-[#E2E8F0] w-[80px]">
                  Target
                </th>
                <th className="text-left text-sm font-medium text-[#020817] py-1.5 pl-3 pr-2 border-r border-[#E2E8F0] w-[80px]">
                  Reference
                </th>
                <th className="text-left text-sm font-medium text-[#020817] py-1.5 pl-3 pr-2 border-r border-[#E2E8F0] w-[120px]">
                  Approx. time
                </th>
                <th className="text-left text-sm font-medium text-[#020817] py-1.5 pl-3 pr-2 w-[300px]">
                  Description
                </th>
                {/* PAYMENT COMMENTED OUT
                <th className="text-center text-sm font-medium text-[#020817] py-1.5 pl-2 pr-3 w-[76px]">
                  Cost
                </th>
                */}
              </tr>
            </thead>
            <tbody>
              {renderCategoryRow('Standard', 'standard', categories.standard)}
              {renderCategoryRow('BERTScore family', 'bertscore', categories.bertscore)}
              {renderCategoryRow('COMET family', 'comet', categories.comet)}
              {renderCategoryRow('Google / MetricX family', 'metricx', categories.metricx)}
            </tbody>
          </table>
          </div>
        </div>

        {/* Footer -- Order Summary and Checkout (never overlaps table; gap above preserves table space) */}
        <div className="shrink-0 mt-3 sm:mt-4 mb-0 p-3 sm:p-4 bg-[#F9FAFB] rounded-lg border border-[#E2E8F0]/80">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1 space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-[#6B7280]">Total Segments:</span>
                <span className="text-[#020817] font-medium">{totalSegments}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#6B7280]">Total Characters:</span>
                <span className="text-[#020817] font-medium">{totalCharacters}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-[#6B7280]">Estimated Delivery Time</span>
                <span className="text-[#020817] font-medium">{estimatedDeliveryTime}</span>
              </div>
              {/* PAYMENT COMMENTED OUT
              <div className="border-t border-[#E2E8F0] pt-3 mt-3">
                <div className="flex justify-between">
                  <span className="text-base font-semibold text-[#020817]">Total Price</span>
                  <span className="text-2xl font-bold text-[#020817]">
                    {isPromocodeApplied ? '$0' : `$${Math.round(totalPrice * 100) / 100}`}
                  </span>
                </div>
              </div>
              */}
            </div>

            <div className="flex-1 space-y-4">
              {/* PAYMENT COMMENTED OUT - Promocode
              <div className="relative">
                <input
                  type="text"
                  placeholder="Enter promocode"
                  value={promocode}
                  onChange={(e) => {
                    setPromocode(e.target.value);
                    setIsPromocodeApplied(false);
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleApplyPromocode();
                    }
                  }}
                  className="w-full px-3 py-2 pr-20 border border-[#E2E8F0] rounded-md text-sm focus:outline-none focus:ring-0 shadow-[0px_1px_2px_0px_#0000000D]"
                />
                <button
                  onClick={handleApplyPromocode}
                  className={`absolute right-1 top-1/2 -translate-y-1/2 px-4 py-1.5 text-sm font-medium transition-colors ${
                    isPromocodeApplied
                      ? 'bg-[#22C55E] text-white rounded-md shadow-[0px_1px_2px_0px_#0000000D]'
                      : 'bg-white text-[#2269DC] hover:bg-[#F9FAFB]'
                  }`}
                >
                  {isPromocodeApplied ? 'Applied!' : 'Apply'}
                </button>
              </div>
              */}

              <div className="space-y-2">
                <div>
                  <div className="text-sm font-medium text-[#020817]">Email Address</div>
                  <div className="text-xs text-[#6B7280] mt-1">
                    We'll send you updates and the completed metrics report via email.
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                      const nextValue = e.target.value;
                      setEmail(nextValue);
                      if (emailError && (isValidEmail(nextValue) || !nextValue.trim())) {
                        setEmailError('');
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleCheckoutClick();
                      }
                    }}
                    onBlur={() => {
                      if (!email.trim()) return;
                      if (!isValidEmail(email)) {
                        setEmailError('Please enter a valid email address');
                      }
                    }}
                    placeholder="your.email@example.com"
                    className={`flex-1 px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-0 shadow-[0px_1px_2px_0px_#0000000D] ${
                      emailError ? 'border-[#EF4444]' : 'border-[#E2E8F0]'
                    }`}
                  />
                </div>
                {emailError && (
                  <div className="text-xs text-[#EF4444]">{emailError}</div>
                )}
                <div className="text-xs text-[#6B7280]">
                  By starting the metrics run, you agree to receive emails from Alconost Inc.
                </div>
              </div>

              {/* Checkout Button */}
              <div className="flex items-center gap-2">
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-3 bg-white text-[#020817] text-base font-medium rounded-md border border-[#E2E8F0] hover:bg-[#F1F5F9] transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCheckoutClick}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-3 bg-[#020817] text-white text-base font-medium rounded-md hover:bg-[rgb(17,24,39)] transition-colors disabled:bg-[#E2E8F0] disabled:text-[#9CA3AF] disabled:cursor-not-allowed"
                >
                  <span className="inline-flex items-center justify-center gap-2">
                    {isSubmitting && (
                      <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                    )}
                    {isSubmitting ? 'Running...' : 'Run Metrics'}
                  </span>
                </button>
              </div>

              {/* PAYMENT COMMENTED OUT - Payment Methods
              <div className="flex items-center gap-4 justify-center flex-wrap ">
                <img src={visa} alt="VISA" className="h-6 w-[43px]" />
                <img src={mastercard} alt="Mastercard" className="h-6 w-[43px]" />
                <img src={paypal} alt="PayPal" className="h-6 w-[43px]" />
                <img src={jcb} alt="JCB" className="h-6 w-[43px]" />
                <img src={dci} alt="Diners Club International" className="h-6 w-[43px]" />
                <img src={americanExpress} alt="American Express" className="h-6 w-[43px]" />
                <img src={discover} alt="Discover" className="h-6 w-[43px]" />
              </div>
              <p className="text-[10px] leading-[14px] text-[#6B7280] !mt-1">
                <a 
                  href="https://www.2checkout.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#3B82F6] hover:underline"
                >
                  2CheckOut.com Inc.
                </a>
                {' '}(Ohio, USA) is an authorized seller of goods and services for{' '}
                <a 
                  href="https://www.alconost.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-[#3B82F6] hover:underline"
                >
                  Alconost Inc.
                </a>
              </p>
              */}

            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MetricsCartModal;
